<template>
  <v-navigation-drawer
    width="300"
    v-if="$vuetify.display.mobile"
    style="z-index: 999999"
    :style="[{ 'position': $vuetify.display.mobile ? 'fixed' : 'absolute' }]"
    v-model="navigationOpen"
    temporary
    class="mobile-nav-drawer">
    <v-list>
      <template v-for="menuItem in menuItems" :key="menuItem.title">
        <v-list-item 
          v-if="!menuItem.children"
          :title="menuItem.title" 
          :href="menuItem.href" 
          :variant="menuItem.variant"
          :target="menuItem.target"
          :active="isActive(menuItem.href)"
          active-class="menu-active"
          class="menu-list-item">
        </v-list-item>
        <v-list-group v-else :value="menuItem.title">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              :title="menuItem.title"
              class="menu-list-item" />
          </template>
          <v-list-item
            v-for="childItem in menuItem.children"
            :key="childItem.title"
            :title="childItem.title"
            :href="childItem.href"
            :target="childItem.target"
            :active="isActive(childItem.href)"
            active-class="menu-active"
            :class="['menu-list-item', { 'new-badge': childItem.isNew }]" />
        </v-list-group>
      </template>
    </v-list>
    
    <template v-slot:append>
      <div class="pa-4">
        <v-btn 
          color="primary" 
          block 
          size="large" 
          :href="bookingUrl" 
          target="_blank"
          class="book-now-mobile-btn">
          Book Now!
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
  
  <v-app-bar 
    class="top-menu d-flex justify-center" 
    name="app-bar" 
    flat 
    :style="[{ 'position': $vuetify.display.mobile ? 'fixed' : 'absolute' }]"
    :elevation="scrolled ? 4 : 0"
    :color="scrolled ? 'background' : 'transparent'"
    :height="$vuetify.display.mobile ? 90 : 120">
    
    <a href="/" class="text-decoration-none">
      <img class="top-logo py-2 px-4" src="/img/logo%20horizontal.webp" alt="Jasmine Therapies" />
    </a>

    <v-spacer></v-spacer>

    <div class="menu-items-container" v-if="!$vuetify.display.mobile">
      <template v-for="menuItem in menuItems" :key="menuItem.title">
        <v-btn 
          v-if="!menuItem.children"
          class="menu-item mx-2" 
          color="primary" 
          :variant="menuItem.variant" 
          :href="menuItem.href"
          :target="menuItem.target"
          :class="{ 'menu-active': isActive(menuItem.href) }">
          {{ menuItem.title }}
        </v-btn>
        <v-menu v-else open-on-hover location="bottom" width="300">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="menu-item mx-2"
              color="primary"
              :variant="menuItem.variant"
              :class="{ 'menu-active': isParentActive(menuItem) }">
              {{ menuItem.title }}
              <v-icon end icon="$mdi-chevron-down"></v-icon>
            </v-btn>
          </template>
          <v-list class="menu-dropdown">
            <v-list-item
              v-for="childItem in menuItem.children"
              :key="childItem.href"
              :title="childItem.title"
              :href="childItem.href"
              :target="childItem.target"
              :active="isActive(childItem.href)"
              active-class="menu-active"
              :class="{ 'new-badge': childItem.isNew }" />
          </v-list>
        </v-menu>
      </template>
    </div>

    <v-app-bar-nav-icon 
      v-if="$vuetify.display.mobile" 
      variant="text" 
      @click.stop="navigationOpen = !navigationOpen"
      aria-label="Open navigation menu"
      class="mobile-menu-icon"></v-app-bar-nav-icon>
  </v-app-bar>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';

interface MenuChild {
  href: string;
  title: string;
  target: string;
  isNew?: boolean;
}

interface MenuItem {
  title: string;
  variant: 'flat' | 'tonal' | 'text' | 'elevated' | 'outlined' | 'plain';
  href?: string;
  target?: string;
  children?: MenuChild[];
}

export default defineComponent({
  name: 'HeaderNavView',

  components: {
  },

  data() {
    const navigationOpen: boolean = null;
    const variantPlain: "flat" | "tonal" | "text" | "elevated" | "outlined" | "plain" = "text";
    
    return {
      navigationOpen,
      scrolled: false,
      bookingUrl: BookingUrls.Default,
      menuItems: [
        {
          href: '/',
          title: 'Home',
          variant: variantPlain,
          target: '_self'
        },
        {
          title: 'Services',
          variant: variantPlain,
          children: [
            {
              href: '/services/beauty-skincare',
              title: 'Beauty & Skincare',
              target: '_self',
              isNew: true
            },
            {
              href: '/services/head-and-neck-massage',
              title: 'Head & Neck Massage',
              target: '_self'
            },
            {
              href: '/services/lymphatic-drainage',
              title: 'Lymphatic Drainage',
              target: '_self'
            },
            {
              href: '/services/pain-relief-massage',
              title: 'Pain Relief Massage',
              target: '_self'
            },
            {
              href: '/services/pregnancy-massage',
              title: 'Pregnancy Massage',
              target: '_self'
            },
            {
              href: '/services/reflexology',
              title: 'Reflexology',
              target: '_self'
            },
            {
              href: '/services/relaxing-massage',
              title: 'Relaxing Massage',
              target: '_self'
            },
            {
              href: '/services/sports-massage',
              title: 'Sports Massage',
              target: '_self'
            }
          ]
        },
        {
          href: '/gift-vouchers',
          title: 'Gift Vouchers',
          variant: variantPlain,
          target: '_self'
        },
        {
          href: '/join-our-team',
          title: 'Join our Team',
          variant: variantPlain,
          target: '_self'
        },
        {
          href: '#contact-us',
          title: 'Contact',
          variant: variantPlain,
          target: '_self'
        },
      ] as MenuItem[]
    };
  },
  mounted() {
    // Add scroll event listener for header effects
    window.addEventListener('scroll', this.handleScroll);
  },
  
  beforeUnmount() {
    // Remove event listeners when component is destroyed
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      if (window.scrollY > 50) {
        this.scrolled = true;
      } else {
        this.scrolled = false;
      }
    },
    
    isActive(href: string): boolean {
      // Guard against SSR - window is not available server-side
      if (typeof window === 'undefined') {
        return false;
      }
      if (href === '/') {
        return window.location.pathname === '/' && !window.location.hash;
      }
      if (href.startsWith('#')) {
        return window.location.hash === href;
      }
      if (href.startsWith('/#')) {
        return window.location.hash === href.substring(1);
      }
      return window.location.pathname === href;
    },

    isParentActive(menuItem: { children?: Array<{ href: string }> }): boolean {
      if (!menuItem.children) {
        return false;
      }
      return menuItem.children.some((childItem) => this.isActive(childItem.href));
    }
  }
});
</script>

<style scoped>
.top-menu {
  padding-left: calc((100vw - 1400px) / 2) !important;
  padding-right: calc((100vw - 1400px) / 2) !important;
  transition: all 0.3s ease;
  z-index: 1000;
}

.content {
  width: 100%;
  max-width: 1400px;
}

.drawer-header {
  background-color: rgba(4, 35, 26, 0.8);
}

.mobile-nav-drawer {
  background-color: rgb(var(--v-theme-background)) !important;
}

.menu-list-item {
  margin-bottom: 4px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.menu-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.book-now-mobile-btn {
  border-radius: 12px;
  font-weight: 600;
  transition: transform 0.2s ease;
}

.book-now-mobile-btn:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
}

.menu-item {
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
}

.menu-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: rgb(var(--v-theme-primary));
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.menu-item:hover::after,
.menu-active::after {
  width: 80%;
}

.top-logo {
  width: 332px;
  height: auto;
  max-width: calc(100vw - 150px);
  min-width: 100px;
  max-height: 100px;
  transition: all 0.3s ease;
}

.account-btn {
  border-radius: 30px;
  transition: all 0.3s ease;
}

.mobile-menu-icon {
  transition: transform 0.3s ease;
}

.mobile-menu-icon:hover {
  transform: rotate(90deg);
}


@media (max-width: 1279px) {
  .top-logo {
    /* max-height: 50px; */
    max-width: 250px;
  }
}

@media (max-width: 960px) {
  .top-menu {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}
</style>

<style>
.v-toolbar__content {
  overflow: visible !important;
}
</style>